import * as React from "react";
import { useSelector } from "react-redux";
import * as moment from "moment";
import { createValidator } from "not-valid";

import { QuestionnaireQuestion, QuestionnairesState } from "./questionnaires/model";
import { AnsweredQuestion } from "./model";

export const usePrevious = <T>(value: T): T => {
    const ref = React.useRef<T>();
    React.useEffect(() => {
        ref.current = value;
    }, [value]);
    return ref.current;
};

export const useTimeHasAlreadyPassed = (value: moment.Moment) => {
    return moment().diff(value) > 0;
};

export const useValidateStartBeforeEndDate = (startTime: moment.Moment, endTime: moment.Moment) => {
    return !startTime || !endTime || endTime.diff(startTime) > 0;
};

export const useValidateStartEndDateSameDay = (startTime: moment.Moment, endTime: moment.Moment) => {
    return !startTime || !endTime || endTime.isSame(startTime, "day");
};

export const useNotInThePastValidator = () =>
    createValidator((value: moment.Moment) => value && value.isSameOrAfter(moment()), "Must not be in the past");

export const useQuestionState = (props: QuestionnaireQuestion): AnsweredQuestion => {
    const questionnaire = useSelector((state: { questionnaires: QuestionnairesState }) => state.questionnaires[props.questionnaireKey]);
    const section = questionnaire?.sections?.find(s => s.id === props.sectionId);
    if (section) {
        const sectionQuestions = { ...section.questions };
        return sectionQuestions[props.questionId] ?? {} as AnsweredQuestion;
    }
    return {} as AnsweredQuestion;
};

export const useDebounce = <T>(value: T, delay: number) => {
    // State and setters for debounced value
    const [debouncedValue, setDebouncedValue] = React.useState(value);
    React.useEffect(
        () => {
            // Update debounced value after delay
            const handler = setTimeout(() => {
                setDebouncedValue(value);
            }, delay);
            // Cancel the timeout if value changes (also on delay change or unmount)
            // This is how we prevent debounced value from updating if value is changed ...
            // .. within the delay period. Timeout gets cleared and restarted.
            return () => {
                clearTimeout(handler);
            };
        },
        [value, delay] // Only re-call effect if value or delay changes
    );
    return debouncedValue;
};
